import type { Project } from '@/api/client';
import { isProjectExcludedFromActiveTable } from '@/utils/projectStatuses';

/** Парсит время в формате чч:мм:сс (или чч:мм) в секунды. null — невалидно или пусто (как на сервере). */
export function parseTimeToSeconds(timeStr: string | null | undefined): number | null {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const trimmed = timeStr.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(':').map((p) => parseInt(p, 10));
  if (parts.some((n) => Number.isNaN(n))) return null;
  const [h = 0, m = 0, s = 0] = parts;
  return h * 3600 + m * 60 + s;
}

/** Сумма времени и число проектов не «Готов» с валидным полем time (чч:мм:сс) — для блока «В процессе». */
export function summarizeNonReadyProjectsWithTime(
  projects: Array<{ state: string; time?: string | null }>
): {
  totalSeconds: number;
  countWithTime: number;
} {
  let totalSeconds = 0;
  let countWithTime = 0;
  for (const p of projects) {
    if (isProjectExcludedFromActiveTable(p.state)) continue;
    const sec = parseTimeToSeconds(p.time ?? null);
    if (sec === null) continue;
    countWithTime += 1;
    totalSeconds += sec;
  }
  return { totalSeconds, countWithTime };
}

/** Сумма time и число проектов с валидным time для уже загруженного списка (например готовые за месяц). */
export function summarizeLoadedProjectsWithTime(
  projects: Array<{ time?: string | null }>
): {
  totalSeconds: number;
  countWithTime: number;
} {
  let totalSeconds = 0;
  let countWithTime = 0;
  for (const p of projects) {
    const sec = parseTimeToSeconds(p.time ?? null);
    if (sec === null) continue;
    countWithTime += 1;
    totalSeconds += sec;
  }
  return { totalSeconds, countWithTime };
}

/**
 * UTC-год и месяц события «сдан» (project_completed), в духе SQL ready-by-month:
 * `(occurred_at AT TIME ZONE 'UTC')::date` попадает в этот календарный месяц.
 */
export function getReadyByMonthBucketFromProject(project: Project): {
  year: number;
  month: number;
} | null {
  const fromIso = (iso: string): { year: number; month: number } | null => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
  };
  const events = project.status_events ?? [];
  const completed = events.filter((e) => e.event_type === 'project_completed');
  if (completed.length > 0) {
    let earliest = completed[0]!;
    let earliestMs = Date.parse(earliest.occurred_at);
    if (Number.isNaN(earliestMs)) earliestMs = Infinity;
    for (let i = 1; i < completed.length; i++) {
      const e = completed[i]!;
      const ms = Date.parse(e.occurred_at);
      if (Number.isNaN(ms)) continue;
      if (ms < earliestMs) {
        earliest = e;
        earliestMs = ms;
      }
    }
    return fromIso(earliest.occurred_at);
  }
  if (project.completed_at) return fromIso(project.completed_at);
  return null;
}

/** Первый день месяца в формате API `ready-months` (YYYY-MM-01). */
export function bucketToReadyMonthYearMonth(bucket: { year: number; month: number }): string {
  return `${bucket.year}-${String(bucket.month).padStart(2, '0')}-01`;
}

/** Два месяца «сдан» совпадают (или оба отсутствуют). */
export function areReadyMonthBucketsEqual(
  a: { year: number; month: number } | null,
  b: { year: number; month: number } | null
): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.year === b.year && a.month === b.month;
}

/** Парсинг year_month из API (YYYY-MM-DD) в ключ и числа для запросов. */
export function parseYearMonthKey(ym: string): { year: number; month: number; key: string } {
  const d = new Date(`${ym.slice(0, 10)}T12:00:00.000Z`);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  return { year, month, key: `${year}-${String(month).padStart(2, '0')}` };
}

export function formatMonthLabelRu(ym: string): string {
  const d = new Date(`${ym.slice(0, 10)}T12:00:00.000Z`);
  const month = new Intl.DateTimeFormat('ru-RU', { month: 'long', timeZone: 'UTC' }).format(d);
  const year = d.getUTCFullYear();
  return `${month} ${year}`;
}

export function formatSecondsToHMS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
}

export function toNumberSeconds(v: string | number): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
}
